<script lang="ts" setup>
/* eslint-disable perfectionist/sort-imports */
import { computed } from 'vue'
import IconInfo from '~icons/material-symbols/info-outline'
import type { FinancingListItem } from '~/utils/financing-flow/Financing'
import SimulationTooltip from '~/components/simulation/Tooltip.vue'
import { INSTALLATION_TIME_RANGES } from '~/constants/installationTime'
import { getDifferenceInDays } from '~/utils/date'

interface Props {
  item: FinancingListItem
}

const props = defineProps<Props>()

const { t } = useI18n()

const installationDaysCalculated = computed(() => {
  const { item } = props
  if (!item.formalized_at) return 0

  const formalizedDate = new Date(item.formalized_at)

  if (item.status === 'approved' && item.installed_at) {
    const installedDate = new Date(item.installed_at)
    return getDifferenceInDays(installedDate, formalizedDate)
  }

  if (item.status === 'canceled' || item.status === 'reproved') {
    const stoppedDate = item.updated_at ? new Date(item.updated_at) : new Date()
    return getDifferenceInDays(stoppedDate, formalizedDate)
  }

  const currentDate = new Date()
  return getDifferenceInDays(currentDate, formalizedDate)
})

const installationDisplayText = computed(() => {
  const { item } = props

  if (item.status === 'canceled' || item.status === 'reproved') {
    return '-'
  }

  const currentDays = installationDaysCalculated.value
  const maxDaysForProgress = 90

  return currentDays >= maxDaysForProgress
    ? `> ${maxDaysForProgress} dias`
    : `${currentDays} ${currentDays === 1 ? 'dia' : 'dias'}`
})

const shouldShowDash = computed(() => installationDisplayText.value === '-')

const installationProgressInfo = computed(() => {
  const currentDays = installationDaysCalculated.value
  const maxDaysForProgress = 90
  const progressPercentage = Math.min((currentDays / maxDaysForProgress) * 100, 100)

  let progressBarColor = 'bg-feedback-positive-pure'

  if (currentDays > INSTALLATION_TIME_RANGES.EXCELLENT
    && currentDays <= INSTALLATION_TIME_RANGES.GOOD) {
    progressBarColor = 'bg-feedback-warning-pure'
  }
  else if (currentDays > INSTALLATION_TIME_RANGES.GOOD) {
    progressBarColor = 'bg-feedback-negative-pure'
  }

  return {
    percentage: progressPercentage,
    showFire: currentDays >= maxDaysForProgress,
    colorClass: progressBarColor,
    isBold: currentDays >= maxDaysForProgress,
  }
})
</script>

<template>
  <div class="installation-time">
    <div class="flex items-center gap-1">
      <span class="fonts-body-small-regular text-neutral-low-light">
        {{ t('financing.list.headers.installing_time') }}
      </span>

      <div
        class="tooltip-wrapper"
        @click.stop.prevent
        @contextmenu.stop.prevent
        @mousedown.stop.prevent
        @mouseup.stop.prevent
        @mousemove.stop.prevent
        @mouseenter.stop
        @mouseleave.stop
      >
        <SimulationTooltip
          position="top"
          variant="default"
          :text="t('financing.list.headers.installing_time_tooltip')"
          :teleport-to-body="true"
        >
          <IconInfo
            class="text-neutral-low-light w-4 h-4 cursor-help icon-aligned"
            :title="t('financing.list.headers.installing_time_tooltip')"
            @click.stop.prevent
            @contextmenu.stop.prevent
            @mousedown.stop.prevent
            @mouseup.stop.prevent
          />
        </SimulationTooltip>
      </div>
    </div>
    <div class="flex items-center gap-2 mt-[-4px]">
      <template v-if="shouldShowDash">
        <span class="fonts-body-medium-regular w-[60px]">
          {{ installationDisplayText }}
        </span>
      </template>
      <template v-else>
        <span
          class="fonts-body-medium-regular w-[60px]"
          :class="{
            'text-feedback-negative-pure': installationProgressInfo.showFire,
            'fonts-body-medium-bold': installationProgressInfo.isBold,
          }"
        >
          {{ installationDisplayText }}
        </span>

        <div class="progress-bar-container">
          <div class="progress-bar-background">
            <div
              class="progress-bar-fill"
              :class="installationProgressInfo.colorClass"
              :style="{ width: `${installationProgressInfo.percentage}%` }"
            />

            <span
              v-if="installationProgressInfo.showFire"
              class="fire-emoji-floating"
            >🔥</span>
          </div>
        </div>
      </template>
    </div>
  </div>
</template>

<style lang="scss" scoped>
.installation-time {
  .progress-bar-container {
    @apply w-[80px] relative pr-[20px];
  }

  .progress-bar-background {
    @apply w-full h-1 bg-neutral-low-light rounded-full relative overflow-visible;
    @apply bg-neutral-low-light bg-opacity-light;

  }

  .progress-bar-fill {
    @apply h-full rounded-full transition-all duration-300 overflow-hidden;
  }

  .fire-emoji-floating {
    @apply absolute right-[-2px] top-1/2 transform -translate-y-1/2 text-12px leading-1 block;
  }

  .tooltip-wrapper {
    @apply relative pointer-events-auto;
  }

  .icon-aligned {
    @apply align-middle mt-[-3px];
  }
}
</style>
